import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axiosInstance from '../axiosConfig';
import { styled } from '@mui/material/styles';
import AppTheme from './shared-theme/AppTheme';
import Stack from '@mui/material/Stack';
import ColorModeSelect from './shared-theme/ColorModeSelect';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import MuiCard from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import NfcRoundedIcon from '@mui/icons-material/NfcRounded';
import Divider, { dividerClasses } from '@mui/material/Divider';
import { useAlert } from './shared/CustomAlert';
import { DateUtils } from "../utils/DateUtils";

const GOOGLE_REVIEW_QR_TYPE = "gr";

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
        maxWidth: '450px',
    },
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    ...theme.applyStyles('dark', {
        boxShadow:
        'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
}));

const HomeContainer = styled(Stack)(({ theme }) => ({
    height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
    minHeight: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        zIndex: -1,
        inset: 0,
        backgroundImage:
        'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
        backgroundRepeat: 'no-repeat',
        ...theme.applyStyles('dark', {
        backgroundImage:
            'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
        }),
    },
}));

function ReviewPage(props) {
  const [searchParams] = useSearchParams(); 
  const qrCodeIdFromParams = searchParams.get('id');
  const qrType = searchParams.get("t");
  const [qrCodeIdNotRegisteredMessage, setQrCodeNotRegisteredMessage] = useState('');
  const { showAlert } = useAlert();

  const fetchStoreData = useCallback(async () => {
    try {
      if(!qrCodeIdFromParams || !qrType) {
        showAlert('Invalid qr scanned', 'error');
      }
      const response = await axiosInstance.post('/get-qr-code', {
        qrCodeId: qrCodeIdFromParams,
        formattedDate: DateUtils.getTodayFormatted()
      });
      const store = response.data;
      if (qrType === GOOGLE_REVIEW_QR_TYPE) {
        if (store && store.businessPlaceId) {
          const redirectUrl = `https://search.google.com/local/writereview?placeid=${store.businessPlaceId}`;
          window.location.href = redirectUrl;
        } else {
          console.error("Store not found or missing businessPlaceId");
        }
      }
    } catch (error) {
      console.error("Error fetching store data:", error);
      setQrCodeNotRegisteredMessage("Smart stand is not registered");
    }
  }, [qrCodeIdFromParams, qrType, showAlert]);

  useEffect(() => {
    fetchStoreData();
  }, [fetchStoreData]);

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <HomeContainer>
        <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem' }} />
        <Card variant="outlined">
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                borderColor: 'divider',
                borderRadius: 1,
                color: 'text.secondary',
                [`& .${dividerClasses.root}`]: {
                mx: 1,
                },
            }}
            >
                <NfcRoundedIcon sx={{ fontSize: 36, marginRight: 0 }}/>
                <Divider orientation="vertical" flexItem />
            <Typography
                variant="h4"
                sx={{ fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
            >
                Smart Stand
          </Typography>
        </Box>
        { qrCodeIdNotRegisteredMessage ? 
      <Box
      sx={{ 
        display: 'flex',
        flexDirection: 'column', // Align items vertically
        alignItems: 'left'     // Center horizontally
      }}
      >
        
        <Typography variant="body2" gutterBottom sx={{ display: 'block', fontSize: 'clamp(1.1rem, 1.75vw, 1.3rem)' }}>
            { qrCodeIdNotRegisteredMessage }
        </Typography>
        {/* <Link
            href={`/?id=${qrCodeIdFromParams}&t=${qrType}`}
            variant="body2"
            sx={{ display: { xs: 'flex', md: 'flex' }, fontSize: 'clamp(1.5rem, 2.5vw, 1.75rem)'}}
        >
            Start set up this smart stand!
        </Link> */}
        <Button 
          variant="contained" 
          href={`/?id=${qrCodeIdFromParams}&t=${qrType}`}
          sx={{ fontSize: 'clamp(1.5rem, 2.5vw, 1.75rem)'}}
        >
         Set up
        </Button>
      </Box> : 
        <Box sx={{ width: '100%' }}>
            <LinearProgress />
        </Box>
      }
        </Card>
      </HomeContainer>
    </AppTheme>
  );
}

export default ReviewPage;
